import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface BenefitProvider {
  id: number;
  provider_code: string;
  provider_name: string;
  policy_reference: string | null;
}

interface BenefitProviderForm {
  provider_code: string;
  provider_name: string;
  policy_reference: string;
}

const emptyForm: BenefitProviderForm = { provider_code: '', provider_name: '', policy_reference: '' };

export function BenefitProviders() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<BenefitProvider[] | null>(null);
  const [editing, setEditing] = useState<'new' | BenefitProvider | null>(null);
  const [form, setForm] = useState<BenefitProviderForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<BenefitProvider[]>('/employee-benefit-providers').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: BenefitProvider) => {
    setForm({ provider_code: item.provider_code, provider_name: item.provider_name, policy_reference: item.policy_reference ?? '' });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof BenefitProviderForm) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/employee-benefit-providers', form);
      else if (editing) await api.put(`/employee-benefit-providers/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: BenefitProvider) => {
    if (!(await confirmDelete(`Delete "${item.provider_name}"?`))) return;
    try {
      await api.delete(`/employee-benefit-providers/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Benefit Providers</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Policy Reference</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.provider_code}</td>
                  <td>{item.provider_name}</td>
                  <td className="muted">{item.policy_reference ?? '—'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No benefit providers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add benefit provider' : 'Edit benefit provider'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Code *
              <input value={form.provider_code} onChange={set('provider_code')} required />
              {errors.provider_code && <small className="error">{errors.provider_code[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.provider_name} onChange={set('provider_name')} required />
              {errors.provider_name && <small className="error">{errors.provider_name[0]}</small>}
            </label>
            <label className="span-2">
              Policy Reference
              <input value={form.policy_reference} onChange={set('policy_reference')} />
              {errors.policy_reference && <small className="error">{errors.policy_reference[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
